import { useForm } from "react-hook-form";
import { useState, useEffect, useContext, useRef } from "react";
import { AppContext } from "../Main";
import Breakdown from "../formParts/Breakdown";
import ExistingAlarms from "../formParts/ExistingAlarms";
import SmokeAlarmAddOn from "../formParts/SmokeAlarmAddOn";
import TestSwitch from "../formParts/TestSwitch";
import CarbonMonoxideRequirements from "../formParts/CarbonMonoxideRequirements";
import Loader from "../UI/Loader";
import prebuildData from "../../data.json";
import { CARBON_MONOXIDE } from "../../utils/utils";

export default function CarbonMonoxide() {
  const {
    setFormType,
    setServiceData,
    setForm,
    step,
    setStep,
    setForwardStep,
    setBackStep,
    setForwardStepTitle,
    breakdown,
    setBreakdown,
    formDefaultValues,
    setFormDefaultValues,
  } = useContext(AppContext);

  //useState to store endpoint data
  const [simProData, setSimProData] = useState({
    opticalSD: [],
    multiSD: [],
    interlinkedHD: [],
    interlinkedCMD: [],
    RFtestSwitch: [],
  });

  // state to manage loading of data from SimPro
  const [loaded, setLoaded] = useState(false);

  // react-hook-form
  const {
    register,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: formDefaultValues ? formDefaultValues : {},
  });

  const formRef = useRef();

  useEffect(() => setFormType(CARBON_MONOXIDE));

  useEffect(() => {
    if (formDefaultValues) {
      // Form has been submitted so user has navigated back from after form submission
      setStep(4);
      setBackStep(() => () => setStep(3));
      setForwardStep(() => () => setForm(9));
    }
  }, [formDefaultValues]);

  const existingAlarmsFormValue = watch("existingAlarms");
  const fluePassingFormValue = watch("fluePassing");
  console.log("fluePassingFormValue", fluePassingFormValue);
  const testSwitchFormValue = watch("testSwitch");
  const smokeAlarmFormValue = watch("smokeAlarm");

  useEffect(() => {
    setForwardStepTitle("Next");
    switch (step) {
      case 0:
        if (existingAlarmsFormValue) {
          setForwardStep(() => () => setStep(1));
        } else {
          setForwardStep(undefined);
        }
        setBackStep(undefined);
        break;
      case 1:
        setBackStep(() => () => setStep(0));
        if (fluePassingFormValue) {
          setForwardStep(() => () => setStep(2));
        } else {
          setForwardStep(undefined);
        }
        break;
      case 2:
        setBackStep(() => () => setStep(1));
        if (testSwitchFormValue) {
          setForwardStep(() => () => setStep(3));
        } else {
          setForwardStep(undefined);
        }
        break;
      case 3:
        setBackStep(() => () => setStep(2));
        setForwardStepTitle("Get my quote");
        if (smokeAlarmFormValue) {
          setForwardStep(
            () => () =>
              formRef.current.dispatchEvent(
                new Event("submit", { cancelable: true, bubbles: true })
              )
          );
        } else {
          setForwardStep(undefined);
        }
        break;
      case 4:
        setBackStep(() => () => setStep(3));
        setForwardStep(() => () => {
          setForm(9);
        });
        break;
      default:
        setBackStep(undefined);
        setForwardStep(undefined);
        break;
    }
  }, [
    fluePassingFormValue,
    smokeAlarmFormValue,
    existingAlarmsFormValue,
    setBackStep,
    setForm,
    setForwardStep,
    setForwardStepTitle,
    setStep,
    step,
    testSwitchFormValue,
  ]);

  // fetch data from SimPro
  useEffect(() => {
    prebuildData.forEach((prebuild) => {
      if (prebuild.ID === 47691) {
        setSimProData((prevState) => ({
          ...prevState,
          opticalSD: prebuild,
        }));
      }
      if (prebuild.ID === 55829) {
        setSimProData((prevState) => ({
          ...prevState,
          multiSD: prebuild,
        }));
      }
      if (prebuild.ID === 47692) {
        setSimProData((prevState) => ({
          ...prevState,
          interlinkedHD: prebuild,
        }));
      }
      if (prebuild.ID === 47693) {
        setSimProData((prevState) => ({
          ...prevState,
          interlinkedCMD: prebuild,
        }));
      }
      if (prebuild.ID === 47696) {
        setSimProData((prevState) => ({
          ...prevState,
          RFtestSwitch: prebuild,
        }));
      }
    });
    setLoaded(true);
  }, []);

  const onSubmit = (data) => {
    console.log(data);
    function calculateTotals() {
      // initilised breakdown of user choices
      let breakdown = [];
      const simproPrebuildsForJob = [];

      // add to breakdown array based on user choices
      if (data.landings > 0) {
        breakdown.push({
          name: "Hall/landing detector",
          qty: data.landings,
          price: simProData?.opticalSD?.TotalEx * data.landings,
          time: simProData?.opticalSD?.LaborTime * data.landings,
          prebuildIds: [simProData?.opticalSD?.ID],
        });
      }

      if (data.separateLivingKitchen === "yes") {
        breakdown.push({
          name: "Living room/kitchen detector",
          qty: 1,
          price:
            simProData?.opticalSD?.TotalEx + simProData?.interlinkedHD?.TotalEx,
          time:
            simProData?.opticalSD?.LaborTime +
            simProData?.interlinkedHD?.LaborTime,
          prebuildIds: [
            simProData?.opticalSD?.ID,
            simProData?.interlinkedHD?.ID,
          ],
        });
      }

      if (data.separateLivingKitchen === "no") {
        breakdown.push({
          name: "Combined living/kitchen detector",
          qty: 1,
          price: simProData?.multiSD?.TotalEx,
          time: simProData?.multiSD?.LaborTime,
          prebuildIds: [simProData?.multiSD?.ID],
        });
      }

      if (data.bedrooms > 0) {
        breakdown.push({
          name: "Bedroom detector",
          qty: data.bedrooms,
          price: simProData?.opticalSD?.TotalEx * data.bedrooms,
          time: simProData?.opticalSD?.LaborTime * data.bedrooms,
          prebuildIds: [simProData?.opticalSD?.ID],
        });
      }

      if (data.testSwitch === "yes") {
        breakdown.push({
          name: "Test switch",
          qty: 1,
          price: simProData?.RFtestSwitch?.TotalEx,
          time: simProData?.RFtestSwitch?.LaborTime,
          prebuildIds: [simProData?.RFtestSwitch?.ID],
        });
      }
      if (data.fluePassing > 0) {
        breakdown.push({
          name: "Carbon monoxide detector",
          qty: data.fluePassing,
          price: simProData?.interlinkedCMD?.TotalEx * data.fluePassing,
          time: simProData?.interlinkedCMD?.LaborTime * data.fluePassing,
          prebuildIds: [simProData?.interlinkedCMD?.ID],
        });
      }

      return breakdown;
    }

    setBreakdown(calculateTotals());
    setStep(4);
    setServiceData({
      isRewiring: false,
      data,
      breakdown: calculateTotals(),
    });
    setFormDefaultValues(data);
  };

  //Conditional component to switch between journey steps
  const conditionalComponent = () => {
    switch (step) {
      case 0:
        return (
          <ExistingAlarms
            register={register}
            setValue={setValue}
            formName={CARBON_MONOXIDE}
            defaultValueExistingAlarms={formDefaultValues?.existingAlarms}
          />
        );
      case 1:
        return (
          <CarbonMonoxideRequirements
            register={register}
            watch={watch}
            setValue={setValue}
            defaultValueFluePassing={formDefaultValues?.fluePassing}
          />
        );
      case 2:
        return (
          <TestSwitch
            register={register}
            setValue={setValue}
            defaultValueTestSwitch={formDefaultValues?.testSwitch}
          />
        );
      case 3:
        return (
          <SmokeAlarmAddOn
            register={register}
            setValue={setValue}
            watch={watch}
            defaultValueBedRoomsRequired={formDefaultValues?.bedRoomsRequired}
            defaultValueBedrooms={formDefaultValues?.bedrooms}
            defaultValueLandings={formDefaultValues?.landings}
            defaultValueSeparateLivingKitchen={
              formDefaultValues?.separateLivingKitchen
            }
            defaultValueSmokeAlarm={formDefaultValues?.smokeAlarm}
          />
        );
      case 4:
        return (
          <Breakdown
            formName={CARBON_MONOXIDE}
            breakdown={breakdown}
            watch={watch}
          />
        );
      default:
        return <ExistingAlarms register={register} setStep={setStep} />;
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
        {/* Component switcher */}
        {loaded ? conditionalComponent() : <Loader />}
      </form>
    </div>
  );
}
